import { PROJECT_CONFIG } from '../config/uploadConfig';

/**
 * Human labels for project status and priority enums.
 *
 * My Projects, the editor project list and the editor project page all show
 * the same statuses; keep one mapping so creators and editors see the same
 * words ("Client Review", not "CLIENT_REVIEW").
 */

const STATUS_LABELS: Record<string, string> = {
  VIDEO_SUBMITTED: 'Video Submitted',
  EDITOR_ASSIGNED: 'Editor Assigned',
  REQUIREMENTS_REVIEW: 'Requirements Review',
  EDITING_IN_PROGRESS: 'Editing in Progress',
  DRAFT_READY: 'Draft Ready',
  CLIENT_REVIEW: 'Client Review',
  REVISION_REQUESTED: 'Revision Requested',
  FINAL_APPROVAL: 'Final Approval',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

/**
 * Where a project stands, from the creator's side. Every status belongs to
 * exactly one group, so counts and filters built on these always add up.
 */
export type StatusGroup = 'needs-review' | 'in-progress' | 'waiting' | 'done' | 'cancelled';

const STATUS_GROUPS: Record<string, StatusGroup> = {
  VIDEO_SUBMITTED: 'waiting',
  EDITOR_ASSIGNED: 'in-progress',
  REQUIREMENTS_REVIEW: 'in-progress',
  EDITING_IN_PROGRESS: 'in-progress',
  REVISION_REQUESTED: 'in-progress',
  DRAFT_READY: 'needs-review',
  CLIENT_REVIEW: 'needs-review',
  FINAL_APPROVAL: 'done',
  DELIVERED: 'done',
  CANCELLED: 'cancelled',
  // Legacy values still stored on some older projects.
  NEW: 'waiting',
  PENDING: 'waiting',
  ASSIGNED: 'in-progress',
  'IN PROGRESS': 'in-progress',
  COMPLETED: 'done',
};

/** The group a status belongs to, or null for an unknown or missing status. */
export const getStatusGroup = (status?: string | null): StatusGroup | null => {
  if (!status) return null;
  return STATUS_GROUPS[status.trim().toUpperCase()] ?? null;
};

/** "CLIENT_REVIEW" -> "Client Review". Unknown values (legacy "In Progress") pass through. */
export const formatStatusLabel = (status?: string | null): string => {
  if (!status) return '';
  return STATUS_LABELS[status.toUpperCase()] ?? status;
};

export type PriorityChipColor = 'error' | 'warning' | 'info' | 'default';

const PRIORITY_CONFIG: Record<string, { label: string; color: PriorityChipColor }> = {
  URGENT: { label: 'Urgent', color: 'error' },
  HIGH: { label: 'High', color: 'warning' },
  MEDIUM: { label: 'Medium', color: 'info' },
  LOW: { label: 'Low', color: 'default' },
};

/** "URGENT" -> "Urgent". Missing priority reads as "Normal". */
export const formatPriorityLabel = (priority?: string | null): string => {
  if (!priority) return 'Normal';
  return PRIORITY_CONFIG[priority.toUpperCase()]?.label ?? priority;
};

/** MUI chip colour for a project priority (Urgent is the only red one). */
export const getPriorityChipColor = (priority?: string | null): PriorityChipColor => {
  if (!priority) return 'default';
  return PRIORITY_CONFIG[priority.toUpperCase()]?.color ?? 'default';
};

/** Delivered and cancelled projects are finished; editors can no longer change them. */
export const isFinishedStatus = (status?: string | null): boolean => {
  const s = (status || '').toUpperCase();
  return s === 'DELIVERED' || s === 'CANCELLED';
};

/**
 * Revisions this project allows. The backend sends the owner's plan allowance
 * (Free 2, Pro 5, Pro Max 8) as `revisionLimit`; older deployments don't, so
 * fall back to the Free allowance.
 */
export const resolveRevisionLimit = (revisionLimit?: number | null): number => {
  if (typeof revisionLimit === 'number' && Number.isFinite(revisionLimit)) {
    // Plans use -1 for "unlimited". Infinity keeps every `count >= limit`
    // check correct without special cases (it used to fall back to 2).
    return revisionLimit < 0 ? Number.POSITIVE_INFINITY : revisionLimit;
  }
  return PROJECT_CONFIG.maxEditRevisions;
};

/** A revision allowance for display: "8", or "unlimited". */
export const formatRevisionLimit = (limit: number): string =>
  Number.isFinite(limit) ? String(limit) : 'unlimited';
